import { createModel } from '@rematch/core';
import { RootModel } from './RootState';
import { apiClient } from '../services/ApiClient';
import type { Product } from '../models/Product';
import { dispatchEvent } from '../services/EventService';

export type ProductComparisonState = {
    loadingProductIds: string[];
    selectedProductIds: string[];
    selectedProducts: Product[];
    expiredAt?: string;
};

export const initialState: ProductComparisonState = {
    loadingProductIds: [],
    selectedProductIds: [],
    selectedProducts: []
};

export const productComparisonState = createModel<RootModel>()({
    state: initialState,
    reducers: {
        reset() {
            return initialState;
        },
        setSelectedProducts(state, selectedProducts: Product[]) {
            return {
                ...state,
                loadingProductIds: [],
                selectedProductIds: selectedProducts.map(p => p.productId),
                selectedProducts
            };
        },
        addLoadingProductId(state, productId: string) {
            if (state.loadingProductIds.includes(productId)) {
                return state;
            }
            return {
                ...state,
                loadingProductIds: [...state.loadingProductIds, productId]
            };
        },
        removeLoadingProductId(state, productId: string) {
            if (!state.loadingProductIds.includes(productId)) {
                return state;
            }
            return {
                ...state,
                loadingProductIds: state.loadingProductIds.filter(
                    id => id !== productId
                )
            };
        },
        setSelectedProductIds(state, selectedProductIds: string[]) {
            dispatchEvent({
                type: 'bri.change_comparing_products',
                isInteraction: true,
                productIds: [...selectedProductIds]
            });
            return {
                ...state,
                selectedProductIds,
                selectedProducts: selectedProductIds
                    .map(
                        id =>
                            state.selectedProducts.find(
                                p => p.productId === id
                            ) as Product
                    )
                    .filter(p => p)
            };
        },
        setSelectedProduct(state, product: Product) {
            const index = state.selectedProductIds.indexOf(product.productId);
            if (index >= 0) {
                const selectedProducts = state.selectedProductIds
                    .map(id => {
                        if (id === product.productId) {
                            return product;
                        }
                        return state.selectedProducts.find(
                            sp => sp.productId === id
                        ) as Product;
                    })
                    .filter(p => p);
                return {
                    ...state,
                    selectedProducts
                };
            }
            return state;
        },
        clearSelectedProducts(state) {
            if (state.selectedProductIds.length > 0) {
                dispatchEvent({
                    type: 'bri.change_comparing_products',
                    isInteraction: true,
                    productIds: []
                });
            }
            return initialState;
        },
        removeProduct(state, productId: string) {
            const index = state.selectedProductIds.indexOf(productId);
            if (index >= 0) {
                const selectedProductIds = state.selectedProductIds.filter(
                    id => id !== productId
                );
                const selectedProducts = selectedProductIds.map(
                    id =>
                        state.selectedProducts.find(
                            p => p.productId === id
                        ) as Product
                );
                dispatchEvent({
                    type: 'bri.change_comparing_products',
                    isInteraction: true,
                    productIds: selectedProductIds
                });
                return {
                    ...state,
                    selectedProductIds,
                    selectedProducts
                };
            }
            return state;
        },
        setExpiredAt(state, expiredAt: string) {
            return { ...state, expiredAt };
        }
    },
    effects: dispatch => ({
        async toggleProductSelection(productId: string, state) {
            const { selectedProductIds } = state.productComparisonState;
            const exists = selectedProductIds.includes(productId);
            const newSelectedProductIds = (
                exists
                    ? selectedProductIds.filter(p => p !== productId)
                    : [...selectedProductIds, productId]
            ).slice(0, 3);
            dispatch.productComparisonState.setSelectedProductIds(
                newSelectedProductIds
            );
            if (!exists && selectedProductIds.length < 3) {
                await dispatch.productComparisonState.loadSelectedProduct(
                    productId
                );
            }
        },
        async loadSelectedProduct(productId: string, state) {
            dispatch.productComparisonState.addLoadingProductId(productId);
            const product = await apiClient.product.getProductByProductId({
                params: { productId }
            });
            if (product.jsonResult) {
                dispatch.productComparisonState.setSelectedProduct(
                    product.jsonResult
                );
            } else {
                dispatch.productComparisonState.setSelectedProductIds(
                    state.productComparisonState.selectedProductIds.filter(
                        id => id !== productId
                    )
                );
            }
            dispatch.productComparisonState.removeLoadingProductId(productId);
        }
    })
});
